import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  PanelsTopLeft,
  Image,
  MessageSquareText,
  LogOut,
  Menu,
  X,
  Bell,
  ChevronRight,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  CircleUserRound,
  Sparkles,
  Building,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const navGroups = [
  {
    label: 'Overview',
    items: [{ to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true }],
  },
  {
    label: 'Tracker',
    items: [{ to: '/tracker', label: 'Web Tracker', icon: Sparkles }],
  },
  {
    label: 'Website content',
    items: [
      { to: '/branches', label: 'Branches', icon: Building },
      { to: '/gallery', label: 'Gallery', icon: Image },
      { to: '/portfolios', label: 'Portfolio', icon: PanelsTopLeft },
      { to: '/banners', label: 'Banners', icon: Image },
    ],
  },
  {
    label: 'Customers',
    items: [{ to: '/contacts', label: 'Contact enquiries', icon: MessageSquareText }],
  },
];

const titles = {
  '/': ['Dashboard', 'Your website at a glance'],
  '/portfolios': ['Portfolio', 'Manage your projects and case studies'],
  '/banners': ['Banners', 'Manage homepage and promotional banners'],
  '/contacts': ['Contact enquiries', 'Review and manage incoming enquiries'],
};

const COLLAPSE_KEY = 'adminSidebarCollapsed';

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

function Brand({ collapsed }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-white shadow-sm">
        <img src="/logo.png" alt="PMS Logo" className="h-6 w-6" />
      </div>
      {!collapsed && (
        <div className="min-w-0">
          <p className="text-[15px] font-bold leading-tight tracking-tight text-ink">
            PMS <span className="text-brand-700">Admin</span>
          </p>
          <p className="text-[11px] text-ink-muted">Pixelmind Solutions</p>
        </div>
      )}
    </div>
  );
}

function SidebarContent({ collapsed, onNavigate, onLogout }) {
  return (
    <>
      <div
        className={`flex h-[68px] shrink-0 items-center border-b border-line ${collapsed ? 'justify-center px-2' : 'px-5'}`}
      >
        <Brand collapsed={collapsed} />
      </div>
      <nav
        aria-label="Main navigation"
        className="flex-1 space-y-6 overflow-y-auto px-3 py-5"
      >
        {navGroups.map((group) => (
          <div key={group.label}>
            {!collapsed && (
              <p className="mb-2 px-3 text-xs font-medium text-slate-500">{group.label}</p>
            )}
            <div className="space-y-1">
              {group.items.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={onNavigate}
                  title={collapsed ? label : undefined}
                  className={({ isActive }) =>
                    `group relative flex items-center gap-3 rounded-lg py-2.5 text-sm font-medium transition ${collapsed ? 'justify-center px-2' : 'px-3'
                    } ${isActive
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-ink'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span className="absolute inset-y-2 left-0 w-[3px] rounded-r-full bg-brand-500" />
                      )}
                      <Icon size={18} className="shrink-0" />
                      {!collapsed && <span className="truncate">{label}</span>}
                      {collapsed && <span className="sr-only">{label}</span>}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="shrink-0 border-t border-line p-3">
        <button
          onClick={onLogout}
          title={collapsed ? 'Sign out' : undefined}
          className={`flex w-full items-center gap-3 rounded-lg py-2.5 text-sm font-medium text-slate-600 transition hover:bg-red-50 hover:text-red-700 ${collapsed ? 'justify-center px-2' : 'px-3'
            }`}
        >
          <LogOut size={18} className="shrink-0" />
          {!collapsed && 'Sign out'}
          {collapsed && <span className="sr-only">Sign out</span>}
        </button>
      </div>
    </>
  );
}

export default function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [pageTitle] = titles[location.pathname] || [
    'PMS Admin',
    'Website management',
  ];
  const name = admin?.name || admin?.fullName || admin?.email || 'Administrator';

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const toggleCollapsed = () => {
    setCollapsed((value) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, value ? '0' : '1');
      } catch {
        /* storage unavailable: the toggle still works for this session */
      }
      return !value;
    });
  };

  // Close the profile menu on outside click or Escape; close drawer on Escape
  useEffect(() => {
    const onPointer = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const sidebarWidth = collapsed ? 'lg:w-[76px]' : 'lg:w-[256px]';
  const contentPad = collapsed ? 'lg:pl-[76px]' : 'lg:pl-[256px]';

  return (
    <div className="min-h-screen bg-page">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[60] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:shadow-pop"
      >
        Skip to content
      </a>

      <aside
        className={`fixed inset-y-0 left-0 z-30 hidden flex-col border-r border-line bg-white transition-[width] duration-200 lg:flex ${sidebarWidth}`}
      >
        <SidebarContent collapsed={collapsed} onLogout={handleLogout} />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-slate-900/50"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <aside
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            className="relative flex h-full w-[280px] max-w-[85vw] flex-col bg-white shadow-pop"
          >
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-4 z-10 rounded-lg p-2 text-slate-500 hover:bg-slate-100"
              aria-label="Close menu"
            >
              <X size={19} />
            </button>
            <SidebarContent
              collapsed={false}
              onNavigate={() => setMobileOpen(false)}
              onLogout={handleLogout}
            />
          </aside>
        </div>
      )}

      <div className={`min-h-screen transition-[padding] duration-200 ${contentPad}`}>
        <header className="sticky top-0 z-20 flex h-[68px] items-center justify-between gap-3 border-b border-line bg-white/90 px-4 backdrop-blur-md sm:px-6">
          <div className="flex min-w-0 items-center gap-2">
            <button
              onClick={() => setMobileOpen(true)}
              className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
              aria-label="Open menu"
            >
              <Menu size={21} />
            </button>
            <button
              onClick={toggleCollapsed}
              className="hidden rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-ink lg:inline-flex"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-pressed={collapsed}
            >
              {collapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
            </button>
            <div className="min-w-0">
              <nav aria-label="Breadcrumb" className="hidden items-center gap-1.5 text-xs text-slate-500 sm:flex">
                <span>Workspace</span>
                <ChevronRight size={13} />
                <span className="font-medium text-ink">{pageTitle}</span>
              </nav>
              <p className="truncate text-base font-semibold tracking-tight text-ink sm:mt-0.5 sm:text-lg">
                {pageTitle}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <button
              className="relative rounded-lg border border-line p-2.5 text-slate-500 transition hover:bg-brand-50 hover:text-brand-700"
              title="Notifications"
              aria-label="Notifications"
            >
              <Bell size={18} />
              <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-brand-500" />
            </button>
            <div className="hidden h-8 w-px bg-line sm:block" />
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="flex items-center gap-2.5 rounded-lg p-1.5 transition hover:bg-slate-50"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                  <CircleUserRound size={21} />
                </div>
                <div className="hidden text-left sm:block">
                  <p className="max-w-36 truncate text-sm font-semibold text-ink">{name}</p>
                  <p className="text-xs text-slate-500">Administrator</p>
                </div>
                <ChevronDown size={15} className="hidden text-slate-400 sm:block" />
              </button>
              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-30 mt-2 w-56 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-line bg-white py-1 shadow-pop"
                >
                  <div className="border-b border-line px-4 py-3">
                    <p className="truncate text-sm font-semibold text-ink">{name}</p>
                    <p className="text-xs text-slate-500">Administrator</p>
                  </div>
                  <button
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      handleLogout();
                    }}
                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-red-50 hover:text-red-700"
                  >
                    <LogOut size={16} />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main id="main-content" className="mx-auto max-w-[1500px] p-4 sm:p-6">
          <Outlet />
        </main>
        <footer className="px-6 pb-6 text-xs text-slate-500">
          © {new Date().getFullYear()} PMS Website Admin. All rights reserved.
        </footer>
      </div>
    </div>
  );
}
